import { createContext, useState, FC, useEffect, useContext } from 'react';
import apiUrl from '../../assets/apiUrl';
import file from '../../common/file';
import folder from '../../common/folder';
import AuthContext, { getAccessToken } from '../Auth';
import FilesMethods from './FilesMethods';
import FoldersMethods from './FoldersMethods';
import UpdatesListener from './UpdatesListener';

interface IFileSystemContext {
  useFiles: () => [file[], React.Dispatch<React.SetStateAction<file[]>>];
  useFolders: () => [folder[], React.Dispatch<React.SetStateAction<folder[]>>];
  useFilesMethods: () => typeof FilesMethods;
  useFoldersMethods: () => typeof FoldersMethods;
  isLoading: boolean;
}

const defaultState = {
  useFiles: () => useState<file[]>([]),
  useFolders: () => useState<folder[]>([]),
  useFilesMethods: () => FilesMethods,
  useFoldersMethods: () => FoldersMethods,
  isLoading: true,
};

const FileSystemContext = createContext<IFileSystemContext>(defaultState);
const systemFolders = ['.thumbnails', '.plugins'];

export const FileSystemProvider: FC = ({ children }) => {
  const useFilesMethods = () => FilesMethods;
  const useFoldersMethods = () => FoldersMethods;
  const [isLoading, setIsLoading] = useState(true);

  const [files, setFiles] = useState<file[]>([]);
  const useFiles = (): [
    file[],
    React.Dispatch<React.SetStateAction<file[]>>
  ] => {
    return [files, setFiles];
  };
  const [folders, setFolders] = useState<folder[]>([]);
  const useFolders = (): [
    folder[],
    React.Dispatch<React.SetStateAction<folder[]>>
  ] => {
    return [folders, setFolders];
  };

  const { isAuthenticated, fetchWrapper } = useContext(AuthContext);

  const removeUnusedThumbnails = (
    param_folders: folder[],
    param_files: file[]
  ) => {
    const thumbnailFolder = param_folders.find(
      (e) => e.title === '.thumbnails'
    );
    if (!thumbnailFolder) {
      return [];
    }
    const thumbnailFiles = param_files.filter(
      (e) => e.folder_uid === thumbnailFolder.folder_uid
    );
    const toRemove: file[] = [];
    thumbnailFiles.forEach((e) => {
      const isFound = param_files.find((b) => b.file_uid === e.filename);
      if (!isFound) {
        toRemove.push(e);
      }
    });
    return toRemove;
  };
  useEffect(() => {
    async function fetchData() {
      const token = await getAccessToken();
      const res = await (
        await fetchWrapper(apiUrl, {
          headers: { 'x-access-token': token },
        })
      ).json();
      const foundFolders = res.folders
        .filter(
          (e: any) =>
            systemFolders.includes(e.title) && e.parent_folder === null
        )
        .map((e: any) => e.title);
      const toCreate: string[] = [];
      systemFolders.forEach((e) => {
        !foundFolders.includes(e) && toCreate.push(e);
      });
      const promises = toCreate.map(
        (e) =>
          new Promise(async (resolve) => {
            const res = await fetchWrapper(
              `${apiUrl}/folder/create?title=${e}`,
              {
                method: 'POST',
                headers: { 'x-access-token': token },
              }
            );
            resolve(res);
          })
      );
      Promise.all(promises).then(async (value) => {
        setFiles(res.files);
        setFolders(([] as any[]).concat(res.folders, value));
        setIsLoading(false);

        const unusedThumbnails = removeUnusedThumbnails(
          ([] as any[]).concat(res.folders, value),
          res.files
        );

        for (const thumbnail of unusedThumbnails) {
          await fetch(`${apiUrl}/file/${thumbnail.file_uid}`, {
            method: 'DELETE',
            headers: { 'x-access-token': token },
          });
        }
      });
    }
    isAuthenticated && fetchData();
  }, [isAuthenticated, fetchWrapper]);

  return (
    <FileSystemContext.Provider
      value={{
        useFiles,
        useFolders,
        useFilesMethods,
        useFoldersMethods,
        isLoading,
      }}
    >
      <UpdatesListener>{children}</UpdatesListener>
    </FileSystemContext.Provider>
  );
};

export { FileSystemContext, systemFolders };
